import { type Appearance } from '@clerk/types';

export const clerkAppearance: Appearance = {
  variables: {
    // Colors - using design system colors with static values for now
    colorPrimary: '#004CC0', // Baseball blue (Athletic Precision rebrand)
    colorDanger: '#ef4444',
    colorSuccess: '#22c55e',
    colorWarning: '#f59e0b',
    colorNeutral: '#666666',
    
    // Typography - matching our design system
    fontFamily: '"Space Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontFamilyButtons: '"Oswald", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    
    // Font sizes - matching design tokens
    fontSize: '16px',
    fontSizeSm: '14px',
    
    // Font weights
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    
    // Border radius - matching design tokens
    borderRadius: '8px', // Medium from design tokens
    
    // Spacing - based on 4px unit system
    spacingUnit: '4px',
  },
  
  elements: {
    
    // Card styling to match our design system
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '12px', // Large from design tokens
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      padding: '24px', // Match the p-6 from AuthModal
    },
    
    // Form elements
    formFieldLabel: {
      fontFamily: '"Oswald", sans-serif',
      fontSize: '14px',
      fontWeight: 500,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      color: '#666666',
    },
    
    formFieldInput: {
      borderRadius: '8px',
      fontSize: '16px',
      padding: '12px 16px',
      backgroundColor: '#ffffff',
      borderColor: '#e0e0e0',
      '&:hover': {
        borderColor: '#cccccc',
      },
      '&:focus': {
        borderColor: '#004CC0',
        boxShadow: '0 0 0 3px rgba(0, 76, 192, 0.1)',
      },
    },
    
    // Buttons - matching our design system
    formButtonPrimary: {
      fontFamily: '"Oswald", sans-serif',
      fontSize: '16px',
      fontWeight: 600,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      backgroundColor: '#004CC0',
      color: 'white',
      borderRadius: '8px',
      padding: '12px 24px',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      },
      '&:active': {
        transform: 'scale(0.98)',
      },
    },
    
    formButtonSecondary: {
      fontFamily: '"Oswald", sans-serif',
      fontSize: '16px',
      fontWeight: 500,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      backgroundColor: 'transparent',
      color: '#1a1a1a',
      borderColor: '#e0e0e0',
      borderRadius: '8px',
      padding: '12px 24px',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#f8f8f8',
        borderColor: '#cccccc',
      },
    },
    
    // Headers - using Bebas Neue for main headings
    headerTitle: {
      fontFamily: '"Bebas Neue", sans-serif !important',
      fontSize: '36px !important',
      letterSpacing: '1.5px !important',
      textTransform: 'uppercase !important',
      color: '#1a1a1a !important',
    },
    
    headerSubtitle: {
      fontFamily: '"Oswald", sans-serif !important',
      fontSize: '18px !important',
      fontWeight: '500 !important',
      letterSpacing: '0.5px !important',
      color: '#666666 !important',
    },
    
    // Modal overlay
    modalBackdrop: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    
    // Modal close button
    modalCloseButton: {
      top: '16px',
      right: '16px',
    },
    
    // Links
    link: {
      color: '#004CC0',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    
    // Social buttons
    socialButtonsBlockButton: {
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#f8f8f8',
        borderColor: '#cccccc',
      },
    },
    
    // Avatar
    userButtonAvatarBox: {
      borderRadius: '50%',
      border: '2px solid #004CC0',
    },
    
    // Footer links
    footerActionLink: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontSize: '14px',
      color: '#666666',
      '&:hover': {
        color: '#004CC0',
      },
    },
    
    // Identity preview
    identityPreviewText: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontSize: '14px',
    },
    
    // Form header
    formHeaderTitle: {
      fontFamily: '"Bebas Neue", sans-serif',
      fontSize: '28px',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    
    formHeaderSubtitle: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontSize: '16px',
      color: 'hsl(var(--muted-foreground))',
    },
    
    // Alert messages
    alert: {
      borderRadius: '8px',
      fontSize: '14px',
    },
    
    // Badge
    badge: {
      fontFamily: '"Oswald", sans-serif',
      fontSize: '12px',
      fontWeight: 500,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      borderRadius: '6px',
      padding: '4px 8px',
    },
    
    // Profile-specific styling
    profileSectionTitle: {
      fontFamily: '"Bebas Neue", sans-serif !important',
      fontSize: '24px !important',
      fontWeight: '400 !important',
      letterSpacing: '1px !important',
      textTransform: 'uppercase !important',
      color: '#1a1a1a !important',
    },
    
    profileSectionTitleText: {
      fontFamily: '"Bebas Neue", sans-serif !important',
      fontSize: '24px !important',
      fontWeight: '400 !important',
      letterSpacing: '1px !important',
      textTransform: 'uppercase !important',
      color: '#1a1a1a !important',
    },
    
    // Text elements
    text: {
      fontFamily: '"Space Grotesk", sans-serif !important',
      color: '#1a1a1a !important',
    },
    
    // Root element override
    rootBox: {
      fontFamily: '"Space Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important',
    },
    
    // Form container
    form: {
      gap: '16px',
    },
    
    // Form field wrapper
    formFieldRow: {
      marginBottom: '16px',
    },

    // Fix Clerk UI overlap issues - add spacing to action links/buttons
    profileSectionContent: {
      marginBottom: '8px',
    },

    profileSectionPrimaryButton: {
      marginTop: '12px',
      display: 'block',
    },

    // Accordion trigger and content spacing
    accordionTriggerButton: {
      marginTop: '12px',
    },

    // Menu items in profile sections
    menuButton: {
      marginTop: '8px',
    },

    // Nav buttons spacing
    navbarButton: {
      marginBottom: '4px',
    },

    // Action cards (like email/connected accounts sections)
    profileSectionItem: {
      marginBottom: '12px',
    },

    profileSectionItemList: {
      gap: '12px',
    },

    // Connected accounts specific styling
    socialButtonsProviderIcon: {
      marginRight: '8px',
    },

    // Alternative button styling (used for "Connect account" type links)
    alternativeMethodsBlockButton: {
      marginTop: '16px',
    },

    // Form button reset styling
    formButtonReset: {
      marginTop: '12px',
    },

    // User preview sections
    userPreview: {
      marginBottom: '8px',
    },

    // Identity preview row
    identityPreview: {
      marginBottom: '12px',
    },

    // Connected accounts - target the specific button
    'button__connectedAccounts': {
      marginTop: '16px',
    },

    'menuButton__connectedAccounts': {
      marginTop: '16px',
    },

    // Connected accounts section content
    'profileSectionContent__connectedAccounts': {
      gap: '16px',
    },

    // Profile section item list for connected accounts
    'profileSectionItemList__connectedAccounts': {
      marginBottom: '12px',
    },

    // Active device section
    activeDevice: {
      marginBottom: '12px',
    },

    // Account switcher trigger
    userButtonTrigger: {
      gap: '8px',
    },

    // "Switch to this plan" button in plan detail drawer
    planDetailAction: {
      fontFamily: '"Oswald", sans-serif !important',
      fontSize: '14px !important',
      fontWeight: '600 !important',
      letterSpacing: '0.5px !important',
      textTransform: 'uppercase !important',
      backgroundColor: '#004CC0 !important',
      color: '#ffffff !important',
      borderRadius: '8px !important',
      padding: '10px 20px !important',
    },
  },
  
  layout: {
    // Mobile responsive settings
    mobileBreakpoint: '768px',
    
    // Minimal shadows as per design system
    boxShadow: {
      card: '0 2px 8px rgba(0,0,0,0.08)',
      cardHover: '0 4px 12px rgba(0,0,0,0.1)',
      modal: '0 -4px 20px rgba(0,0,0,0.1)',
    },
    
    // Animation settings
    animations: {
      duration: {
        fast: '0.15s',
        normal: '0.2s',
        slow: '0.3s',
      },
      curve: {
        default: 'ease',
        ease: 'ease',
      },
    },
  },
};